import * as React from 'react'

import { useTranslation } from 'react-i18next'

import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom'

import { AlertCircle, Home, RefreshCw } from 'lucide-react'

import { logger } from '@/lib/logger'

import { useAuthStore } from '@/stores/auth'

import { paths } from '@/config/paths'

import { SmartButton } from '@/components/ui-custom/SmartButton'

export default function RouteErrorPage() {
  const { t } = useTranslation(['common'])
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const error = useRouteError()

  React.useEffect(() => {
    // This catches render/load errors for route elements.
    logger.error('[RouteErrorPage] Route error', error)
  }, [error])

  const title: string = t('common:errors.unexpectedTitle', { defaultValue: 'Something went wrong' })

  let message: string = t('common:errors.unexpectedMessage', {
    defaultValue: "We couldn't load this page. Please try again, or contact support.",
  })

  let devDetails: string | null = null

  if (isRouteErrorResponse(error)) {
    message = `${error.status} ${error.statusText}`

    // Some loaders/actions throw responses with `data`
    if (typeof error.data === 'string' && error.data.trim()) {
      devDetails = error.data
    } else if (error.data && typeof error.data === 'object' && 'message' in error.data) {
      const maybeMessage = (error.data as { message?: unknown }).message
      if (typeof maybeMessage === 'string') devDetails = maybeMessage
    }
  } else if (error instanceof Error) {
    devDetails = error.message
  } else if (typeof error === 'string') {
    devDetails = error
  }

  return (
    <div role='alert' className='flex min-h-screen w-full items-center justify-center bg-gray-50 p-4'>
      <div className='bg-card text-card-foreground w-full max-w-lg space-y-4 rounded-lg border p-6 shadow-sm'>
        <div className='flex flex-col items-center gap-4 text-center'>
          <div className='bg-destructive/15 rounded-full p-3'>
            <AlertCircle className='text-destructive h-8 w-8' />
          </div>

          <div className='space-y-2'>
            <h2 className='text-xl font-semibold tracking-tight text-red-600'>{title}</h2>
            <p className='text-muted-foreground text-sm'>{message}</p>
          </div>

          {import.meta.env.DEV && devDetails && (
            <pre className='bg-muted text-muted-foreground max-h-40 w-full overflow-auto rounded-md p-3 text-left text-xs'>
              {devDetails}
            </pre>
          )}

          <div className='mt-2 flex w-full flex-col gap-2 sm:flex-row'>
            <SmartButton variant='outline' className='w-full' onClick={() => window.location.reload()}>
              <RefreshCw className='mr-2 h-4 w-4' />
              {t('common:actions.refresh', { defaultValue: 'Reload' })}
            </SmartButton>
            <SmartButton
              variant='outline'
              className='w-full'
              onClick={() => navigate(user ? paths.dashboard.root.getHref() : paths.public.auth.login.getHref())}
            >
              <Home className='mr-2 h-4 w-4' />
              {t('common:actions.goHome', { defaultValue: 'Go Home' })}
            </SmartButton>
          </div>
        </div>
      </div>
    </div>
  )
}
